import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import cx from 'classnames';
import { AbsoluteTimestamp } from '@xingternal/timestamp';
import { Button, TextButton } from '@xingternal/button';
import { compareAsc } from 'date-fns';
import { IconClock } from '@xingternal/icons';
import { usePopOver } from '@xingternal/pop-over';
import { FormField } from '@xingternal/text-field';
import { Meta } from '@xingternal/typography';
import { useMediaListener, BREAKPOINTS } from '@xingternal/util';

import { trackAction } from '../tracking';

// Auxiliary declarative error catcher
import ErrorBoundary from '../lib/ErrorBoundary';

import { TimeSelector } from './TimeSelector';
import { DayPicker } from './DayPicker';
import {
  isValidDay,
  transformDate,
  isDateTimeInTheFuture,
} from './utils/dateTimeHelpers';

import styles from './styles.css';

const Menu = React.lazy(() => import('./MenuLazy'));

const DateTimePickerNonIntl = ({ intl, publishingDate, setPublishAt }) => {
  const breakpoint = useMediaListener();
  const popOver = usePopOver();
  const [date, setDate] = useState('');
  const [dateError, setDateError] = useState(null);
  const [timeError, setTimeError] = useState(null);
  const [time, setTime] = useState('');
  const [isoString, setIsoString] = useState('');
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isCalendarShown, setIsCalendarShown] = useState(true);
  const [isTimerShown, setIsTimerShown] = useState(false);
  const [minimumHourAllowed, setMinimumHourAllowed] = useState(0);
  // TODO: handle errors for these kinds of issues. We need some sort of UI
  // const [err, setErr] = React.useState(null);
  const setErr = () => {};

  const calculateMinimumHourAllowed = (currentDT, enteredDT) => {
    if (transformDate(enteredDT) > transformDate(currentDT)) {
      setMinimumHourAllowed(0);
    } else if (transformDate(enteredDT) === transformDate(currentDT)) {
      setMinimumHourAllowed(currentDT.getHours() + 1);
    } else {
      setMinimumHourAllowed(99);
    }
  };

  const validateDateTime = () => {
    let enteredDT;
    const currentDT = new Date();

    // date must have a valid format e.g. 12.05.50051 is possible
    // by the native input component, but we want to give an error
    // feedback and disable the save button
    if (!isValidDay(date)) {
      setDateError(
        intl.formatMessage({ id: 'COMMBOX_SCHEDULING_DATE_VALIDITY_ERROR' })
      );
      return;
    } else {
      setDateError(null);
    }

    try {
      enteredDT = new Date(`${date}T${time}:00`);
    } catch (e) {
      setDateError(
        intl.formatMessage({ id: 'COMMBOX_SCHEDULING_DATE_VALIDITY_ERROR' })
      );
      return;
    }

    calculateMinimumHourAllowed(currentDT, enteredDT);
    // date & time must be in the future
    if (compareAsc(enteredDT, currentDT) >= 0) {
      setDateError(null);
      setTimeError(null);
      setIsoString(enteredDT.toISOString());
    } else {
      // today, but in the past time/hour wise
      if (date === transformDate(currentDT)) {
        setDateError(null);
        setTimeError(
          intl.formatMessage({ id: 'COMMBOX_SCHEDULING_TIME_FUTURE_ERROR' })
        );
      } else {
        // day in the past
        setDateError(
          intl.formatMessage({ id: 'COMMBOX_SCHEDULING_DATE_FUTURE_ERROR' })
        );
        setTimeError(null);
      }
    }
  };

  const handleSelectTime = (timeAsText) => {
    setTime(timeAsText);
  };
  const handleSubmit = () => {
    if (isDateTimeInTheFuture(date, time)) {
      trackAction({ PropTrackAction: 'social_commbox_scheduling_save' });
      setPublishAt(isoString);
      popOver.handleHide();
    } else {
      validateDateTime();
    }
  };
  const handleReset = () => {
    setDateTime(null);
    setPublishAt(null);
    popOver.handleHide();
  };
  const isHandHeld = () => {
    return breakpoint === BREAKPOINTS.handheld;
  };

  const hideAndTrackClose = () => {
    trackAction({ PropTrackAction: 'social_commbox_scheduling_close' });
    popOver.handleHide();
  };

  const hideAndTrackOpen = () => {
    trackAction({ PropTrackAction: 'social_commbox_scheduling_open' });
    popOver.handleShow();
  };

  const formattedHour = (hour) => (hour < 10 ? `0${hour}` : hour);

  const formattedMinute = (minute) => (minute < 10 ? `0${minute}` : minute);

  const showCalendar = () => {
    setIsTimerShown(false);
    setIsCalendarShown(true);
  };

  const showTimer = () => {
    setIsCalendarShown(false);
    setIsTimerShown(true);
  };

  const setDateTime = (publishingDate) => {
    const scheduledDate = publishingDate
      ? new Date(publishingDate)
      : new Date();
    if (!publishingDate) {
      scheduledDate.setHours(scheduledDate.getHours() + 1, 0);
      if (scheduledDate.getHours() < 6) {
        scheduledDate.setHours(6);
      }
    }
    const newTime = `${formattedHour(
      scheduledDate.getHours()
    )}:${formattedMinute(scheduledDate.getMinutes())}`;
    const newDate = transformDate(scheduledDate);
    setDate(newDate);
    setTime(newTime);
  };

  useEffect(() => {
    validateDateTime();
  }, [date, time]);

  useEffect(() => {
    if (dateError || timeError) {
      setIsButtonDisabled(true);
    } else {
      setIsButtonDisabled(false);
    }
  }, [dateError, timeError]);

  useEffect(() => {
    // Any calls to Date return time in UTC
    setDateTime(publishingDate);
  }, []);

  return (
    <React.Fragment>
      <TextButton
        className={styles.schedulingOpener}
        data-testid={'commbox-scheduling-opening-button'}
        innerRef={popOver.triggerRef}
        onClick={(event) => {
          popOver.show ? hideAndTrackClose() : hideAndTrackOpen();
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <IconClock width={24} height={24} className={styles.clockIcon} />
        {publishingDate ? (
          <span
            className={styles.publishingTime}
            data-testid={'commbox-scheduling'}
          >
            <AbsoluteTimestamp
              date={new Date(publishingDate)}
              variant="shortDayDateTime"
            />
          </span>
        ) : (
          <Meta
            className={cx(styles.publishImmediately, styles.publishImmediately)}
          >
            <FormattedMessage id="COMMBOX_SCHEDULING_IMMEDIATELY" />
          </Meta>
        )}
      </TextButton>
      <ErrorBoundary onError={setErr}>
        <React.Suspense fallback={<div></div>}>
          {popOver.show && (
            <Menu
              className={cx(
                isHandHeld() && styles.menuMobile,
                !isHandHeld() && styles.menuDesktop
              )}
              data-testid={'commbox-scheduling-menu'}
              dimmerTitle="close menu"
              onOutsideClick={() => hideAndTrackClose()}
              show={popOver.show}
              triggerRef={popOver.triggerRef}
            >
              <div
                className={cx(
                  styles.datepickerForm,
                  isHandHeld() && styles.mobileDatepickerForm
                )}
              >
                {isHandHeld() && (
                  <Meta className={styles.mobileDatepickerHeader}>
                    <FormattedMessage id="COMMBOX_SCHEDULING_TITLE" />
                  </Meta>
                )}
                <div className={styles.formFieldsWrapper}>
                  <FormField
                    className={cx(styles.input, dateError && styles.inputError)}
                    data-testid={'commbox-scheduling-date-input'}
                    error={!!dateError}
                    helperText={dateError}
                    label={<FormattedMessage id="COMMBOX_SCHEDULING_DATE" />}
                    onChange={(event) => {
                      // e.target.value can be and empty string
                      // and this breaks the calendar component
                      if (event.target.value.length) {
                        setDate(event.target.value);
                      }
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      !isHandHeld() && showCalendar();
                    }}
                    onFocus={() => {
                      !isHandHeld() && showCalendar();
                    }}
                    size="small"
                    type="date"
                    value={date}
                  />
                  <FormField
                    className={cx(styles.input, timeError && styles.inputError)}
                    data-testid={'commbox-scheduling-time-input'}
                    error={!!timeError}
                    helperText={timeError}
                    label={<FormattedMessage id="COMMBOX_SCHEDULING_TIME" />}
                    onChange={(event) => {
                      setTime(event.target.value || '08:00');
                    }}
                    onFocus={() => {
                      !isHandHeld() && showTimer();
                    }}
                    onClick={() => {
                      !isHandHeld() && showTimer();
                    }}
                    type="time"
                    size="small"
                    value={time}
                  />
                  <Button
                    className={cx(styles.saveButton)}
                    data-testid={'commbox-scheduling-submit-button'}
                    disabled={isButtonDisabled}
                    onClick={handleSubmit}
                    variant="primary"
                    size="small"
                  >
                    <FormattedMessage id="COMMBOX_SCHEDULING_SAVE" />
                  </Button>
                  <Button
                    data-testid={'commbox-scheduling-reset-button'}
                    disabled={isButtonDisabled}
                    onClick={handleReset}
                    variant="tertiary"
                    size="small"
                  >
                    <FormattedMessage id="COMMBOX_SCHEDULING_NOW" />
                  </Button>
                </div>
                {!isHandHeld() && isCalendarShown && (
                  <DayPicker
                    onChangeDay={(date) => {
                      setDate(transformDate(date));
                    }}
                    selectedDay={date}
                  />
                )}
                {!isHandHeld() && isTimerShown && (
                  <TimeSelector
                    onSelectTime={(timeAsText) => handleSelectTime(timeAsText)}
                    selectedTime={time}
                    minimumHourAllowed={minimumHourAllowed}
                  />
                )}
              </div>
            </Menu>
          )}
        </React.Suspense>
      </ErrorBoundary>
    </React.Fragment>
  );
};

DateTimePickerNonIntl.propTypes = {
  intl: PropTypes.object,
  publishingDate: PropTypes.string,
  setPublishAt: PropTypes.func,
};

const DateTimePicker = injectIntl(DateTimePickerNonIntl);

export { DateTimePicker };
